import React from 'react';
import { Alert, Heading, Row, Col, themes, Collapse, Spinner } from '@digitools/honest-react';
import { useTranslation } from 'react-i18next';
import useGet from '../../../../hooks/useGet';
import checkDate from './../../../status/utilities/checkDate';
import formatPhone from './../../../status/utilities/formatPhone';
import formatAddress from './../../../status/utilities/formatAddress';
// @ts-ignore
import styles from './styles.scss';
import { isNotEmpty } from '../../../../utils/fieldValidators';
import useAuthentication from '../../../../hooks/useAuthentication';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import LogRocket from 'logrocket';
const mapGender = (gender) => {
    if (gender) {
        if (gender === 'M') {
            return 'Male';
        }
        else if (gender === 'F') {
            return 'Female';
        }
        else {
            return '-';
        }
    }
};
const isInfoEmpty = (value) => {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        return trimmedValue === '' || trimmedValue === '-';
    }
    return false;
};
export const Info = ({ info, text, restricted, dataPrivate, ...rest }) => {
    if (info === null && restricted) {
        return null;
    }
    const empty = isInfoEmpty(info);
    return (<div className={styles.info} style={{ display: 'flex', paddingLeft: '.5rem', paddingRight: '.5rem' }} {...rest}>
      <strong className={styles.infoLabel}>{text}</strong>
      <span data-private={dataPrivate} style={{ marginLeft: 'auto', textAlign: 'right' }}>
        {empty ? (<>
            <span aria-hidden='true'>-</span>
            <span className='sr-only'>Empty</span>
          </>) : (info)}
      </span>
    </div>);
};
const EmployeeDetails = ({ empNum, empEmailAddress, updateSubsAndLocsCallback }) => {
    const { t } = useTranslation();
    const { error, loading, data,
    // @ts-ignore TODO: type useGet Hook
     } = useGet({
        url: `/status-service/employee/overview/${empNum}`,
    });
    const { user } = useAuthentication();
    const { experience } = useExperience();
    // determine if we should show additional details
    const showDetails = experience === Experience.EMPLOYEE ||
        user?.applicationPermissions?.includes('statusER.claimLeaveStatus.viewEmployeeDetails');
    // If we dont have an empNum then we dont show this component
    if (!empNum) {
        return null;
    }
    if (loading) {
        // @ts-ignore TODO: type Spinner component
        return <Spinner id='employee-details-spinner'/>;
    }
    if (error) {
        LogRocket.error('Error loading employee details', { empNum, error });
        return (<Alert type={'error'} closeable={true}>
        <span>
          <strong>Error!</strong>&nbsp;Something went wrong when retrieving employee information.
        </span>
      </Alert>);
    }
    const employeeFullName = `${data?.firstName} ${data?.lastName}`;
    const subsAndLocs = {
        location: data?.location,
        subsidiary: data?.subsidiary,
        employeeFullName,
    };
    if (updateSubsAndLocsCallback) {
        updateSubsAndLocsCallback(subsAndLocs);
    }
    return (<section aria-labelledby="employee-details-heading" data-testid={'employeeBox'} data-public={true} style={{
            border: `1px solid ${themes.rebrand.palette.grayLighter}`,
            boxShadow: `${themes.rebrand.palette.grayLighter} 1px 1px 1px`,
        }}>
      <Row style={{ paddingBottom: '0px', margin: '0px' }}>
        <Col lg={3} md={12} sm={12} style={{ paddingLeft: '0px', paddingRight: '0px' }}>
          <div style={{ backgroundColor: `${themes.rebrand.palette.primaryDark}`, height: '100%', padding: '1rem' }}>
            <Heading id="employee-details-heading" data-private={true} elemType={'h2'} elemStyle={'h2'} color={'white'}>
              {employeeFullName}
            </Heading>
          </div>
        </Col>
        <Col lg={9} md={12} sm={12} style={{ paddingBottom: '1rem', paddingTop: '1rem' }}>
          <Row style={{ marginBottom: '.5rem' }}>
            <Col lg={6} md={6} sm={12}>
              <Info text={t('employeeIdText')} dataPrivate={true} info={data?.empId}/>
              <Info text={t('ssnSidText')} dataPrivate={true} info={data?.ssn != null && data?.ssn.trim() !== '' ? `***\xa0**\xa0${data?.ssn}` : data?.ssn} restricted={true}/>
              <Info text={t('dateOfBirthText')} dataPrivate={true} info={data?.birthDate} restricted={true}/>
              <Info text={t('dateOfHireText')} dataPrivate={true} info={data?.hireDate}/>
            </Col>
            <Col lg={6} md={6} sm={12}>
              {isNotEmpty(data?.location) || isNotEmpty(data?.subsidiary) || !user?.isLfgUser ? (<>
                  <Info text={t('locationText')} info={data?.location}/>
                  <Info text={t('subsidiaryText')} info={data?.subsidiary}/>
                </>) : (<></>)}
            </Col>
          </Row>
          {showDetails && (<Collapse headerElemType='h3' data-testid={'employeeDetails'} title={`${t('employeeDetails')}`} type='alternate' theme={themes.rebrand}>
              <Row>
                <Col lg={6} md={6} sm={12}>
                  <Info text={t('jobTitleText')} info={data?.jobTitle}/>
                  <Info text={t('supervisorText')} info={data?.supervisorName} dataPrivate={true} restricted={true}/>
                  <Info text={t('salaryAmountText')} info={data?.salaryAmount} restricted={true}/>
                  <Info text={t('salaryModeText')} info={data?.salaryMode}/>
                  <Info text={t('benefitEligibleText')} info={checkDate(data?.benefitEligibleDate)}/>
                  <Info text={t('genderText')} info={mapGender(data?.gender)}/>
                </Col>
                <Col lg={6} md={6} sm={12}>
                  <Info text={t('workAddressText')} info={formatAddress(data?.workAddress1, data?.workCity, data?.workState, data?.workZipCode)} data-private={true}/>
                  <Info text={t('workPhoneText')} info={formatPhone(data?.workPhoneNumber)} dataPrivate={true}/>
                  <Info text={t('homeAddressText')} info={formatAddress(data?.homeAddress1, data?.homeCity, data?.homeState, data?.homeZipCode)} dataPrivate={true}/>
                  <Info text={t('homePhoneText')} info={formatPhone(data?.homePhoneNumber)} dataPrivate={true}/>
                  <Info text={t('emailText')} info={empEmailAddress} dataPrivate={true}/>
                </Col>
              </Row>
            </Collapse>)}
        </Col>
      </Row>
    </section>);
};
export default EmployeeDetails;
